import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { BaselineStanding, STANDING_LABEL } from '@innerhive-internal/innerhive-common';
import {
    CHART_INK,
    describeUnknownStanding,
    hexCentre,
    hexPoints,
    initialsFor,
    STANDING_COLOR,
    STANDING_ORDER,
    STATUS
} from './agencyChartTheme';

export interface IHoneycombCell {
    careMapId: string;
    clientName: string;
    standing: BaselineStanding;
    /** Whether anything was recorded in the recent weeks, which says why an unknown cell is unknown. */
    recordedRecently: boolean;
    /** Their overall score, named in the cell's hover text. */
    score: number;
    /** How many things already happened in the window: falls, ER trips, hospital stays. */
    incidentCount: number;
}

const RADIUS = 30;
const COLUMNS = 6;
const BADGE_R = 8.5;
/** Past this the count stops being a number and becomes "a lot", which the badge has room for. */
const MAX_BADGE_COUNT = 9;

const useStyles = createUseStyles({
    wrap: {
        // A caseload wider than the viewport scrolls itself rather than pushing the page sideways.
        overflowX: 'auto',
        paddingBottom: 4
    },

    svg: {
        display: 'block',
        maxWidth: '100%',
        height: 'auto'
    },

    cell: {
        cursor: 'pointer',

        // No ring, no outline. A caseload map is a field of shapes and a box drawn round one of them
        // reads as a selection that persists, which it does not: the click opens a dialog and leaves.
        '&:focus': {
            outline: 'none'
        },
        // A wash rather than a movement. A turning-token animation was tried here and dropped: with a
        // field of cells under one pointer it fired and reversed constantly, and browsers disagreed
        // enough about transforms on an svg group that it read as glitching rather than as motion.
        '&:hover .ih-hex-face, &:focus-visible .ih-hex-face': {
            fillOpacity: 0.3
        }
    },

    hexFace: {
        transition: 'fill-opacity 120ms ease'
    },

    initials: {
        pointerEvents: 'none',
        fontSize: 15,
        fontWeight: 600
    },

    badgeCount: {
        pointerEvents: 'none',
        // Small enough that two characters clear the badge's edge at this radius.
        fontSize: 9,
        fontWeight: 700,
        fill: '#fff'
    },

    legend: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '6px 18px',
        marginTop: 12
    },

    legendItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontSize: 12.5,
        color: CHART_INK.label
    },

    swatch: {
        width: 11,
        height: 11,
        borderRadius: 3,
        flexShrink: 0
    },

    incidentKey: {
        width: 11,
        height: 11,
        borderRadius: '50%',
        background: STATUS.incident,
        flexShrink: 0
    }
});

/**
 * The caseload as a honeycomb, one cell per client.
 *
 * A list answers "what is outstanding"; this answers "where should I look", which is the question a
 * dispatcher opens the tab with. It holds a whole book of clients in a single glance where twenty-five
 * rows would not, and it borrows the care map's own hexagon so the agency view and the client view
 * read as the same product. Pointy-top, like the care map: the per-client diagram is flat-top because
 * its six EDGES carry the domain labels, which is a different job from tiling a field of cells.
 *
 * Each cell is filled by where the client sits against their own baseline, so a busy but stable client
 * stays quiet and a normally-settled client who has declined stands out.
 *
 * The red badge counts things that already happened, and nothing else. It used to count everything
 * outstanding, which put a badge on most of the map and made the one client who had actually had a
 * fall impossible to pick out.
 */
export const HoneycombCaseloadMap: FC<{
    cells: IHoneycombCell[];
    /** When the tab is narrowed, the clients still in scope. Everyone else stays but recedes. */
    matchingIds?: string[];
    /** Opens the client's detail view. Named for what it does, not for where it happens to lead. */
    onSelectClient: (careMapId: string) => void;
}> = ({ cells, matchingIds, onSelectClient }) => {
    const classes = useStyles();

    if (cells.length === 0) {
        return null;
    }

    // Dropping the non-matching cells would renumber the grid and move every remaining client, which
    // costs the reader the one thing the honeycomb is for: knowing where a given client sits.
    const matching = matchingIds ? new Set(matchingIds) : undefined;

    const columns = Math.min(COLUMNS, cells.length);
    const rows = Math.ceil(cells.length / columns);
    const cellWidth = Math.sqrt(3) * RADIUS;
    // The half-cell stagger on odd rows makes the grid half a cell wider than the column count.
    const width = cellWidth * (columns + (rows > 1 ? 0.5 : 0));
    const height = RADIUS * 1.5 * (rows - 1) + RADIUS * 2;

    return (
        <div data-id="honeycomb-caseload-map">
            <div className={classes.wrap}>
                {/* group, not img: role="img" would make the svg a leaf for assistive tech and hide
                    the cells inside it, which are focusable by keyboard either way. */}
                <svg
                    className={classes.svg}
                    viewBox={`0 0 ${Math.ceil(width)} ${Math.ceil(height)}`}
                    width={Math.ceil(width)}
                    role="group"
                    aria-label={`Caseload of ${cells.length} clients, each shown against their own baseline`}
                >
                    {cells.map((cell, index) => {
                        const column = index % columns;
                        const row = Math.floor(index / columns);
                        const { cx, cy } = hexCentre({ column, row, radius: RADIUS });

                        const colour = STANDING_COLOR[cell.standing];
                        const isDimmed = matching ? !matching.has(cell.careMapId) : false;
                        const points = hexPoints(cx, cy, RADIUS - 1.5);

                        return (
                            <g
                                key={cell.careMapId}
                                className={classes.cell}
                                tabIndex={0}
                                role="button"
                                data-id="honeycomb-cell"
                                data-standing={cell.standing}
                                data-dimmed={String(isDimmed)}
                                opacity={isDimmed ? 0.22 : 1}
                                onClick={() => onSelectClient(cell.careMapId)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onSelectClient(cell.careMapId);
                                    }
                                }}
                            >
                                {/* An SVG title is a text node, so it can carry mp-mask and stay out
                                    of session replay. An HTML title attribute could not. */}
                                <title className="mp-mask">
                                    {cell.standing === 'unknown'
                                        ? describeUnknownStanding(cell)
                                        : `${cell.clientName}: ${cell.score} out of 100, ${STANDING_LABEL[cell.standing].toLowerCase()}`}
                                    {cell.incidentCount > 0
                                        ? `, ${cell.incidentCount} incident${cell.incidentCount === 1 ? '' : 's'} or ER`
                                        : ''}
                                </title>

                                <polygon
                                    className={`${classes.hexFace} ih-hex-face`}
                                    points={points}
                                    fill={colour}
                                    fillOpacity={0.14}
                                    stroke={colour}
                                    strokeWidth={2}
                                />
                                <text
                                    className={`${classes.initials} mp-mask`}
                                    x={cx}
                                    y={cy + 5}
                                    textAnchor="middle"
                                    fill={colour}
                                >
                                    {initialsFor(cell.clientName)}
                                </text>

                                {cell.incidentCount > 0 && (
                                    <>
                                        <circle
                                            cx={cx + RADIUS * 0.52}
                                            cy={cy - RADIUS * 0.52}
                                            r={BADGE_R}
                                            fill={STATUS.incident}
                                            stroke={CHART_INK.surface}
                                            strokeWidth={2}
                                        />
                                        <text
                                            className={classes.badgeCount}
                                            x={cx + RADIUS * 0.52}
                                            y={cy - RADIUS * 0.52 + 3.2}
                                            textAnchor="middle"
                                        >
                                            {cell.incidentCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : cell.incidentCount}
                                        </text>
                                    </>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            <div className={classes.legend}>
                {STANDING_ORDER.map(standing => (
                    <span key={standing} className={classes.legendItem}>
                        <span className={classes.swatch} style={{ background: STANDING_COLOR[standing] }} />
                        {STANDING_LABEL[standing]}
                    </span>
                ))}
                <span className={classes.legendItem}>
                    <span className={classes.incidentKey} />
                    Incident or ER
                </span>
            </div>
        </div>
    );
};
